import { useState } from "react";

import { useAdminJobs, useRequeueJob } from "../../api/queries";
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  Loading,
  Select,
} from "../../ui/primitives";
import { JobStatusBadge } from "../site/OverviewPage";

/** Slower than a customer's own view: this is a monitor, not a progress bar. */
const LIVE_MS = 5000;

export function AdminJobsPage() {
  const [status, setStatus] = useState("failed");
  const jobs = useAdminJobs(status || undefined, LIVE_MS);
  const requeue = useRequeueJob();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label htmlFor="status" className="text-sm font-medium">
          Status
        </label>
        <Select
          id="status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className="w-auto"
        >
          <option value="">All</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
          <option value="pending">Queued</option>
          <option value="succeeded">Succeeded</option>
        </Select>
      </div>

      {requeue.error != null && <ErrorNote error={requeue.error} />}

      {jobs.isLoading ? (
        <Loading />
      ) : jobs.error ? (
        <ErrorNote error={jobs.error} />
      ) : (jobs.data ?? []).length === 0 ? (
        <Empty title="No jobs match this filter" />
      ) : (
        <Card>
          <ul className="divide-y divide-line">
            {(jobs.data ?? []).map((job) => {
              // Only terminal jobs can be requeued: two workers on the same
              // documents is a different problem from running one twice.
              const terminal = !["pending", "running"].includes(job.status);

              return (
                <li key={job.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
                  <JobStatusBadge status={job.status} />
                  <span className="font-medium">{job.organization_name}</span>
                  <span className="text-muted">{job.site_name}</span>
                  <span className="text-muted">{job.type.replace("_", " ")}</span>

                  <span className="ml-auto text-xs tabular-nums text-muted">
                    {new Date(job.created_at).toLocaleString()}
                    {job.attempts > 1 && ` · attempt ${job.attempts}`}
                  </span>

                  <Button
                    size="sm"
                    disabled={!terminal || requeue.isPending}
                    onClick={() => requeue.mutate(job.id)}
                  >
                    Re-run
                  </Button>

                  {job.error && (
                    <p className="w-full text-xs text-bad" role="alert">
                      {job.error}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
